import { useMemo } from "react";

import BaseAnchor from "@/components_v2/Anchor/BaseAnchor";
import {
  SectionResponse,
  SectionStatisticsResponse,
} from "@/redux/survey/types";

import OverviewQuestionCard from "./OverviewQuestionCard";
import SectionStatusTag from "./SectionStatusTag";
import type { SurveyViewDetailsProps } from "../types";

const filterQuestionIds = (
  questionIds: number[],
  filteredQuestions: number[] | null,
): number[] =>
  questionIds.filter(
    (questionId) =>
      filteredQuestions === null || filteredQuestions.includes(questionId),
  );

const getSectionQuestions = (
  section: SectionResponse,
  filteredQuestions: number[] | null,
): number[] => {
  if (section.tabs.length) {
    return section.tabs.reduce<number[]>(
      (acc, tab) =>
        acc.concat(filterQuestionIds(tab.questions, filteredQuestions)),
      [],
    );
  }

  return filterQuestionIds(section.questions, filteredQuestions);
};

const hasVisibleSectionContent = (
  section: SectionResponse,
  filteredQuestions: number[] | null,
): boolean => getSectionQuestions(section, filteredQuestions).length > 0;

const SurveyViewDetails = ({
  sections,
  surveyStatisticsResponse,
  filteredQuestions = null,
}: SurveyViewDetailsProps) => {
  const sortedSections = useMemo(
    () => sections.slice().sort((a, b) => a.index - b.index),
    [sections],
  );

  const statisticsBySectionId = useMemo(() => {
    if (!surveyStatisticsResponse.isSuccess || !surveyStatisticsResponse.data) {
      return new Map<number, SectionStatisticsResponse>();
    }

    return new Map(
      surveyStatisticsResponse.data.map((statistics) => [
        statistics.id,
        statistics,
      ]),
    );
  }, [surveyStatisticsResponse]);

  const anchorItems = useMemo(
    () =>
      sortedSections.map((section) => {
        const statistics = statisticsBySectionId.get(section.id);
        const href = `#section-${section.id}`;

        return {
          key: String(section.id),
          href,
          title: (
            <span className="body-m-medium flex items-center justify-between gap-200 full-width">
              <span className="flex items-center gap-200">
                {section.icon ? (
                  <div
                    className="survey-overview-section-icon-container"
                    style={{ backgroundColor: section.color }}
                  >
                    <img src={section.icon} alt="" width={24} height={24} />
                  </div>
                ) : null}

                {section.title}
              </span>

              {statistics && (
                <div className="survey-view-details-section-status-tag">
                  <SectionStatusTag statistics={statistics} />
                </div>
              )}
            </span>
          ),
        };
      }),
    [sortedSections, statisticsBySectionId],
  );

  return (
    <div className="flex full-width gap-500">
      <aside className="flex-shrink-0" style={{ width: "17rem" }}>
        <BaseAnchor
          className="survey-overview-anchor"
          items={anchorItems}
          offsetTop={100}
          urlSync
          activeItemColor="var(--primary-100)"
        />
      </aside>

      <main className="flex flex-col flex-1 gap-600" style={{ minWidth: 0 }}>
        {sortedSections.map((section) => {
          if (!hasVisibleSectionContent(section, filteredQuestions)) {
            return null;
          }

          const sortedTabs = section.tabs
            .slice()
            .sort((a, b) => a.index - b.index);

          return (
            <section
              key={section.id}
              id={`section-${section.id}`}
              className="flex flex-col gap-400"
            >
              <div className="flex items-center gap-200">
                {section.icon && (
                  <img
                    src={section.icon}
                    alt=""
                    width={36}
                    height={36}
                    className="survey-overview-section-icon"
                    style={{ backgroundColor: section.color }}
                  />
                )}

                <h2 className="header-l-medium">{section.title}</h2>
              </div>

              {sortedTabs.length > 0 ? (
                <div className="flex flex-col gap-400">
                  {sortedTabs.map((tab) => {
                    const tabQuestions = filterQuestionIds(
                      tab.questions,
                      filteredQuestions,
                    );

                    if (tabQuestions.length === 0) {
                      return null;
                    }

                    return (
                      <div key={tab.id} className="flex flex-col gap-300">
                        <h3 className="survey-overview-tab-subtitle header-s-medium">
                          {tab.title}
                        </h3>

                        <div className="flex flex-col gap-300">
                          {tabQuestions.map((questionId, index) => (
                            <OverviewQuestionCard
                              key={questionId}
                              questionId={questionId}
                              questionIndex={index}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-300">
                  {filterQuestionIds(section.questions, filteredQuestions).map(
                    (questionId, index) => (
                      <OverviewQuestionCard
                        key={questionId}
                        questionId={questionId}
                        questionIndex={index}
                      />
                    ),
                  )}
                </div>
              )}
            </section>
          );
        })}
      </main>
    </div>
  );
};

export default SurveyViewDetails;
